import React, { useState, useEffect } from "react";
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  UserGroupIcon,
  StarIcon,
  AcademicCapIcon,
  LanguageIcon,
  UserIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import { Filters, ClassFilterBarProps } from "@/types/fitness-class";

// Enhanced filter options based on FitnessClass type
const durations = ["30", "45", "60", "75", "90", "120"]; // in minutes as strings for display
const languages = [
  "English",
  "Hindi",
  "Marathi",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Bengali",
];
// Hidden filters: formats and classTypes as requested
// const formats = ["live", "onDemand", "private", "group", "workshop"];
const timesOfDay = ["morning", "afternoon", "evening", "night"];
const difficulties = ["beginner", "intermediate", "advanced", "all"];
const ageBands = ["kids", "teens", "adults", "seniors", "all"];
// const classTypes = [
//   "live",
//   "onDemand",
//   "private",
//   "group",
//   "workshop",
//   "challenge",
// ];

// Categories based on instructor types and their specializations
const categories = [
  // Yoga categories
  "Hatha Yoga",
  "Vinyasa Flow",
  "Ashtanga",
  "Yin Yoga",
  "Power Yoga",
  "Restorative Yoga",
  "Prenatal Yoga",
  "Chair Yoga",
  "Hot Yoga",
  "Kundalini",

  // Meditation categories
  "Mindfulness Meditation",
  "Guided Meditation",
  "Mantra Meditation",
  "Chakra Meditation",
  "Walking Meditation",
  "Sleep Meditation",

  // Pranayama categories
  "Basic Breathing",
  "Advanced Pranayama",
  "Therapeutic Breathing",
  "Stress Relief Breathing",
  "Energy Breathing",

  // Zumba categories
  "Classic Zumba",
  "Zumba Gold",
  "Zumba Toning",
  "Aqua Zumba",
  "Zumba Kids",
  "Strong by Zumba",

  // Nutrition categories
  "Weight Management",
  "Diabetic Nutrition",
  "Sports Nutrition",
  "Pregnancy Nutrition",
  "Child Nutrition",
  "Senior Nutrition",

  // Ayurveda categories
  "Ayurvedic Consultation",
  "Dosha Analysis",
  "Panchakarma Guidance",
  "Herbal Medicine",
  "Ayurvedic Lifestyle",

  // Women's wellness
  "Menstrual Health",
  "Pregnancy Wellness",
  "Postpartum Care",
  "Menopause Support",
  "Hormonal Balance",
  "Women's Fitness",

  // Self defense
  "Basic Self Defence",
  "Martial Arts",
  "Women's Self Defence",
  "Krav Maga",
  "Kickboxing",
  "Situational Awareness",

];

const instructorTypes = [
  { value: "yogaInstructor", label: "Yoga Instructor" },
  { value: "pranayamaCoach", label: "Pranayama Coach" },
  { value: "meditationCoach", label: "Meditation Coach" },
  { value: "zumbaInstructor", label: "Zumba Instructor" },
  { value: "nutritionist", label: "Nutritionist" },
  { value: "ayurvedaPractitioner", label: "Ayurveda Practitioner" },
  { value: "womenWellnessCoach", label: "Women Wellness Coach" },
  { value: "selfDefenseInstructor", label: "Self Defense Instructor" },
];

const ClassFilterBar: React.FC<ClassFilterBarProps> = ({
  setFilters,
  filters,
  setCurrentPage,
  setHasMore,
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [instructors, setInstructors] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [instructorMap, setInstructorMap] = useState<Record<string, string>>(
    {}
  );

  // Fetch available instructors from API
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch instructors list
        const instructorsResponse = await fetch("/api/instructors?limit=1000");
        const instructorsData = await instructorsResponse.json();

        if (instructorsData.instructors) {
          const instructorOptions = instructorsData.instructors.map(
            (instructor: { _id: string; name: string }) => ({
              value: instructor._id,
              label: instructor.name,
            })
          );

          // Create a map of ID to name for quick lookup
          const idToNameMap = instructorsData.instructors.reduce(
            (
              acc: Record<string, string>,
              instructor: { _id: string; name: string }
            ) => {
              acc[instructor._id] = instructor.name;
              return acc;
            },
            {}
          );

          setInstructors(instructorOptions);
          setInstructorMap(idToNameMap);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleChange = (
    name: keyof Filters,
    value: string | number | [number, number] | string[] | boolean
  ) => {
    // Convert duration strings to numbers for proper filtering
    if (name === "duration" && typeof value === "string") {
      value = parseInt(value);
    }

    setFilters((prev: Filters) => ({
      ...prev,
      [name]: value,
    }));
    setCurrentPage?.(1);
    setHasMore?.(true);
  };

  const handleClearAll = () => {
    setFilters({
      category: "",
      level: "",
      duration: 0,
      difficulty: "",
      instructor: "",
      instructorType: "all",
      date: "",
      location: "",
      price: [0, 5000],
      language: "",
      classLanguages: "",
      format: "",
      classType: "all",
      ageGroup: "all",
      instructorExperience: 0,
      rating: [0, 5],
      timeOfDay: "",
      specialFeatures: [],
      capacity: 0,
      accessibility: [],
      medicalClearanceRequired: false,
      parentalConsentRequired: false,
      equipmentProvided: [],
      waitlistAvailable: false,
    });
    setCurrentPage?.(1);
    setHasMore?.(true);
  };

  const renderDropdown = (
    label: string,
    name: keyof Filters,
    options: string[] | { value: string; label: string }[],
    icon: React.ReactNode
  ) => {
    // Get the display value for the selected option
    const getDisplayValue = () => {
      const filterValue = filters[name] as string;
      if (!filterValue) return label;

      // Special handling for instructor field - map ID to name
      if (name === "instructor" && instructorMap[filterValue]) {
        return instructorMap[filterValue];
      }

      // For options with value/label structure, find the label
      if (options.length > 0 && typeof options[0] === "object") {
        const found = (options as { value: string; label: string }[]).find(
          (opt) => opt.value === filterValue
        );
        return found ? found.label : filterValue;
      }

      return filterValue;
    };

    return (
      <div className="relative">
        <button
          onClick={() =>
            setActiveDropdown(activeDropdown === name ? null : name)
          }
          className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-[#463a33]/10 rounded-xl hover:border-[#ff7043]/50 focus:outline-none focus:ring-2 focus:ring-[#ff7043]/20 transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-medium text-[#463a33]">
              {getDisplayValue()}
            </span>
          </div>
          {activeDropdown === name ? (
            <ChevronUpIcon className="w-5 h-5 text-[#463a33]" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-[#463a33]" />
          )}
        </button>
        {activeDropdown === name && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-[#463a33]/10 py-2 animate-scaleIn max-h-60 overflow-y-auto">
            {options.length > 0 ? (
              options.map((option, index) => {
                const optionValue =
                  typeof option === "string" ? option : option.value;
                const optionLabel =
                  typeof option === "string" ? option : option.label;
                return (
                  <button
                    key={`${name}-${optionValue}-${index}`}
                    onClick={() => {
                      handleChange(name, optionValue);
                      setActiveDropdown(null);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-[#463a33] hover:bg-[#ff7043]/5 transition-colors duration-200"
                  >
                    {optionLabel}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No options available
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-[#463a33]/10">
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={handleClearAll}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#463a33] hover:bg-[#463a33]/5 rounded-lg transition-colors duration-200"
        >
          <XMarkIcon className="w-5 h-5" />
          Clear All
        </button>
      </div>

      {/* Normal Filters - Most Commonly Used */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[#463a33] uppercase tracking-wide flex items-center gap-2">
          <FireIcon className="w-4 h-4 text-[#ff7043]" />
          Quick Filters
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#463a33]">
              Category
            </label>
            {renderDropdown(
              "Select Category",
              "category",
              categories,
              <AcademicCapIcon className="w-5 h-5 text-[#463a33]" />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#463a33]">
              Instructor Type
            </label>
            {renderDropdown(
              "Select Type",
              "instructorType",
              [{ value: "all", label: "All Types" }, ...instructorTypes],
              <UserIcon className="w-5 h-5 text-[#463a33]" />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#463a33]">
              Duration
            </label>
            {renderDropdown(
              "Select Duration",
              "duration",
              durations.map((d) => ({ value: d, label: `${d} minutes` })),
              <ClockIcon className="w-5 h-5 text-[#463a33]" />
            )}
          </div>
        </div>
      </div>

      {/* Secondary Quick Filters */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#463a33]">
            Instructor
          </label>
          {renderDropdown(
            "Select Instructor",
            "instructor",
            instructors,
            <UserIcon className="w-5 h-5 text-[#463a33]" />
          )}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#463a33]">Language</label>
          {renderDropdown(
            "Select Language",
            "classLanguages",
            languages,
            <LanguageIcon className="w-5 h-5 text-[#463a33]" />
          )}
        </div>
      </div>

      {/* Additional Filters */}
      <div className="space-y-6 mt-8">
        {/* Price Range and Rating in one row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Price Range */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-[#463a33]">
              <CurrencyRupeeIcon className="w-5 h-5 text-[#ff7043]" />
              Price Range (₹{filters.price?.[0] || 0} - ₹
              {filters.price?.[1] || 5000})
            </label>
            <div className="px-2">
              <input
                type="range"
                min="0"
                max="5000"
                step="100"
                value={filters.price?.[1] || 5000}
                onChange={(e) =>
                  handleChange("price", [
                    filters.price?.[0] || 0,
                    Number(e.target.value),
                  ])
                }
                className="w-full h-2 bg-[#463a33]/10 rounded-lg appearance-none cursor-pointer accent-[#ff7043]"
              />
              <div className="flex justify-between text-xs text-[#463a33]/70 mt-1">
                <span>₹{filters.price?.[0] || 0}</span>
                <span>₹{filters.price?.[1] || 5000}</span>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-[#463a33]">
              <StarIcon className="w-5 h-5 text-[#ff7043]" />
              Minimum Rating ({filters.rating?.[0] || 0} -{" "}
              {filters.rating?.[1] || 5} ★)
            </label>
            <div className="px-2">
              <input
                type="range"
                min="0"
                max="5"
                step="0.5"
                value={filters.rating?.[0] || 0}
                onChange={(e) =>
                  handleChange("rating", [
                    Number(e.target.value),
                    filters.rating?.[1] || 5,
                  ])
                }
                className="w-full h-2 bg-[#463a33]/10 rounded-lg appearance-none cursor-pointer accent-[#ff7043]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {Object.entries(filters).some(
        ([key, value]) =>
          (Array.isArray(value) &&
            value.length > 0 &&
            key === "price" &&
            (Number(value[0]) > 0 || Number(value[1]) < 5000)) ||
          (Array.isArray(value) &&
            value.length > 0 &&
            key === "rating" &&
            (Number(value[0]) > 0 || Number(value[1]) < 5)) ||
          (!Array.isArray(value) &&
            value !== "" &&
            value !== 0 &&
            value !== false &&
            value !== "all")
      ) && (
          <div className="mt-6 pt-6 border-t border-[#463a33]/10">
            <h3 className="text-sm font-medium text-[#463a33] mb-3">
              Active Filters:
            </h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => {
                // Skip removed filter types
                if (
                  [
                    "specialFeatures",
                    "accessibility",
                    "equipmentProvided",
                    "medicalClearanceRequired",
                    "parentalConsentRequired",
                    "waitlistAvailable",
                  ].includes(key)
                ) {
                  return null;
                }

                if (Array.isArray(value)) {
                  if (
                    key === "price" &&
                    value.length === 2 &&
                    (Number(value[0]) > 0 || Number(value[1]) < 5000)
                  ) {
                    return (
                      <span
                        key={key}
                        className="px-3 py-1 bg-[#ff7043]/10 text-[#ff7043] rounded-full text-sm"
                      >
                        Price: ₹{value[0]} - ₹{value[1]}
                      </span>
                    );
                  }
                  if (
                    key === "rating" &&
                    value.length === 2 &&
                    (Number(value[0]) > 0 || Number(value[1]) < 5)
                  ) {
                    return (
                      <span
                        key={key}
                        className="px-3 py-1 bg-[#ff7043]/10 text-[#ff7043] rounded-full text-sm"
                      >
                        Rating: {value[0]} - {value[1]} ★
                      </span>
                    );
                  }
                } else if (
                  value !== "" &&
                  value !== 0 &&
                  value !== false &&
                  value !== "all"
                ) {
                  // Get display value for the filter
                  let displayValue = String(value);

                  // Special handling for instructor - show name instead of ID
                  if (key === "instructor" && instructorMap[value as string]) {
                    displayValue = instructorMap[value as string];
                  }

                  // Format the key name for better readability
                  const formatKey = (k: string) => {
                    return (
                      k.charAt(0).toUpperCase() +
                      k
                        .slice(1)
                        .replace(/([A-Z])/g, " $1")
                        .trim()
                    );
                  };

                  return (
                    <span
                      key={key}
                      className="px-3 py-1 bg-[#463a33]/5 text-[#463a33] rounded-full text-sm"
                    >
                      {formatKey(key)}: {displayValue}
                    </span>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}

      <style jsx global>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }

        input[type="range"] {
          -webkit-appearance: none;
          height: 8px;
          background: #463a331a;
          border-radius: 4px;
          outline: none;
        }

        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          background: #ff7043;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
};

export default ClassFilterBar;
